import Loading from '@/components/Loading';
import { Dropdown, DropdownMenuDivider, DropdownMenuItem } from '@/components/UI/Dropdown';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackCellProps, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar';
import { errorToast, successToast } from '@/helpers';
import { _isNil, _notNil } from '@/littledash';
import type { DataTableApiId, DataTableV1 } from '@/model/DataTable.model';
import type { Study, StudyApiId } from '@/model/Study.model';
import InVivoError from '@/model/InVivoError';
import { RiMoreFill } from 'react-icons/ri';
import { useParams } from 'react-router-dom';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { RouteService } from '@/support/RouteService';
import { useModalAction } from '@/utils/modal';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { FC, useCallback, useMemo } from 'react';

interface SamplingSheetDataTable extends DataTableV1 {
  animal_count?: number;
  sample_count?: number;
  created_by?: {
    id: string;
    name: string;
  };
}

const SamplingSheetList: FC = () => {
  const { openModal } = useModalAction();
  const { id: studyId } = useParams<{ id: string }>();

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    query: { include: 'users' },
    options: { onError: { toast: true, slug: 'sample-sheet-list-study-load' } },
  });

  const study = (studyResponse?.body as { data: Study })?.data;
  const isStudyWriteUser = Auth.isWriteUserForStudy(study as Study);

  const { invoke: invokeDeleteDataTable } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/datatables/{dataTableApiId}',
    path: { studyApiId: study?.api_id as StudyApiId, dataTableApiId: 'dtl_empty' },
    invokeOnInit: false,
  });

  const handleDataTableEdit = useCallback(
    (dataTableId: DataTableApiId, fetchTableData: () => Promise<void>): void => {
      openModal('EDIT_DATATABLE', {
        studyId: study?.api_id ?? '',
        dataTableId,
        fetchTableData,
        mode: 'sample_sheet',
      });
    },
    [openModal, study?.api_id]
  );

  const handleDataTableDelete = useCallback(
    (dataTableId: DataTableApiId, fetchTableData: () => Promise<void>): void => {
      openModal('CONFIRM_DELETE_DATATABLE', {
        onClick: async () => {
          try {
            await invokeDeleteDataTable({
              path: { studyApiId: study?.api_id as StudyApiId, dataTableApiId: dataTableId },
            });
            await fetchTableData();
            successToast('Deleted sampling sheet');
          } catch (err) {
            const ivError = new InVivoError('Could not delete sampling sheet', {
              slug: 'delete-sampling-sheet',
              cause: err,
            });
            ExceptionHandler.captureException(ivError);
            errorToast('Could not delete sampling sheet');
          }
        },
      });
    },
    [openModal, invokeDeleteDataTable, study?.api_id]
  );

  const columns: Array<TanStackColumnDef<SamplingSheetDataTable>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Data table name',
        accessorKey: 'name',
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: SamplingSheetDataTable } }) =>
          _notNil(original) && (
            <Link
              to={
                RouteService.web({
                  route: 'studies.dataTables.show',
                  path: { id: studyId, dataTableApiId: original.id },
                  query: { type: 'sample_sheet' },
                }).route
              }
              className="link blue"
            >
              {original.name}
            </Link>
          ),
      },
      {
        id: 'animal_count',
        header: 'Animals',
        accessorFn: (row: SamplingSheetDataTable) => row.animal_count,
        isVisible: true,
        enableSorting: false,
      },
      {
        id: 'sample_count',
        header: 'Samples collected',
        accessorFn: (row: SamplingSheetDataTable) => row.sample_count,
        isVisible: true,
        enableSorting: false,
      },
      {
        id: 'created_at',
        header: 'Created',
        accessorKey: 'created_at',
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: SamplingSheetDataTable } }) =>
          _notNil(original) ? <DateTimeRenderer value={original.created_at} /> : null,
      },
      {
        id: 'created_by',
        header: 'Created by',
        accessorKey: 'created_by',
        isVisible: true,
        enableSorting: false,
        cell: ({ row: { original } }: { row: { original: SamplingSheetDataTable } }) =>
          _notNil(original?.created_by) ? (
            <UserAvatar user={original.created_by} className="ml1 dib" tooltip={original.created_by.name} />
          ) : null,
      },
      {
        id: 'actions',
        isVisible: isStudyWriteUser,
        size: 35,
        enableSorting: false,
        cell: (props: TanStackCellProps<SamplingSheetDataTable>) => {
          const { row, table } = props;
          const { original } = row;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const fetchTableData = (table?.options?.meta as any)?.apiTable?.fetchTableData as
            | (() => Promise<void>)
            | undefined;

          return _notNil(original) && _notNil(fetchTableData) ? (
            <Dropdown
              renderMenu={() => (
                <>
                  <DropdownMenuItem onClick={() => handleDataTableEdit(original.id as DataTableApiId, fetchTableData)}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuDivider />
                  <DropdownMenuItem
                    onClick={() => handleDataTableDelete(original.id as DataTableApiId, fetchTableData)}
                  >
                    <span className="red">Delete</span>
                  </DropdownMenuItem>
                </>
              )}
            >
              <RiMoreFill size={22} />
            </Dropdown>
          ) : null;
        },
      },
    ],
    [isStudyWriteUser, studyId, handleDataTableEdit, handleDataTableDelete]
  );

  if (studyLoading || _isNil(study)) {
    return (
      <div style={{ height: 600 }}>
        <Loading txt="Loading sampling sheets" />
      </div>
    );
  }

  const apiRoute = RouteService.api({
    endpoint: 'GET /api/v1/studies/{studyApiId}/datatables/sample-sheets',
    path: { studyApiId: study?.api_id },
  });

  return (
    <div className="h-100" data-test-component="SamplingSheetList" data-test-element="container">
      <APITable
        apiInfo={{
          type: 'internalApi',
          route: apiRoute.url,
        }}
        columns={columns}
        defaultSortBy={{ id: 'created_at', desc: true }}
        searchPlaceholderText="Search by name or observation"
        reduxTableName="samplingSheets"
        testIdPrefix="samplingSheets"
      />
    </div>
  );
};

export default SamplingSheetList;
